import { css } from '@emotion/react';
import { Skeleton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { ClubLogo, vars, fontSize } from '@scorenco/components';

const style = {
  root: (theme: Theme) => css`
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 2.8rem;

    .event-card_logo {
      width: 1.25rem;
      height: 2rem;
    }

    .event_card_give-score {
      text-align: center;
      font-size: 10px;
      font-style: italic;
    }

    .event-card_scoreboard_live {
      text-align: center;
      font-size: 8px;
      font-weight: bold;
      color: #cc062a;
      text-transform: uppercase;
      margin-top: 0.5rem;
      margin-bottom: 0.25rem;
      display: flex;
      justify-content: center;
      align-items: center;

      &::before {
        content: '';
        height: 0.5rem;
        width: 0.5rem;
        margin-right: 0.25rem;
        background-color: #cc062a;
        border-radius: 9999px;
        display: inline-block;
      }
    }

    .event-card_scoreboard_shoutouts {
      margin: 0;
      font-size: 1.2rem;
      text-align: center;
      font-weight: bold;
      margin-top: 0.25rem;
    }

    .event-card_scores {
      margin-top: 0.25rem;
      margin-bottom: 0.25rem;
      display: flex;
    }

    .event-card_scores_and_shoutouts {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .event-card_date {
      text-align: center;
      font-size: 1.2rem;
      line-height: 1.2;
      font-style: italic;

      > *:first-of-type {
        line-height: 1.2;
        font-style: normal;
        font-weight: 500;
      }
    }

    .event-card_team {
      flex: 1;
      ${fontSize.base(theme)};
      line-height: 1.2;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      padding: 0 ${theme.spacing(2)};
    }

    .event-card_team-away {
      text-align: right;
    }
  `,
  score: css`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    border-radius: 0.25rem;
    text-align: center;
    background-color: var(${vars.color.secondary100});
    width: 1.75rem;
    font-family: var(${vars.font.alt});
    margin: 0 2px;
    letter-spacing: -0.1em;

    &:first-of-type {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  `,
  scoreIsWinner: css`
    background-color: var(${vars.color.secondary100});
    color: var(${vars.color.primary500});
  `,
  scoreSmall: css`
    font-size: 16px;
  `,
};

type ScoreProps = {
  score?: number;
  isWinner: boolean;
};

const Score = ({ score, isWinner }: ScoreProps) => {
  if (score == null) {
    return null;
  }

  return (
    <div
      css={[
        style.score,
        isWinner && style.scoreIsWinner,
        score >= 100 && style.scoreSmall,
      ]}
    >
      {score}
    </div>
  );
};

type GameData = {
  game: {
    home_engagement?: {
      id: number;
      name?: string;
      team?: { id: number; name?: string; logo?: string } | null;
    } | null;
    away_engagement?: {
      id: number;
      name?: string;
      team?: { id: number; name?: string; logo?: string } | null;
    } | null;
    home_score?: number | null;
    away_score?: number | null;
    status?: string;
    home_shootout?: number | null;
    away_shootout?: number | null;
    date?: string | null;
  };
};

export type GameProps = {
  game?: GameData;
};

export const Game = ({ game, ...rest }: GameProps) => {
  const homeTeam = game?.game?.home_engagement;
  const awayTeam = game?.game?.away_engagement;
  const homeScore = game?.game?.home_score;
  const awayScore = game?.game?.away_score;
  const status = game?.game?.status;
  const homeShoutout = game?.game?.home_shootout;
  const awayShoutout = game?.game?.away_shootout;
  const gameDate = game?.game?.date;
  const hasScores = homeScore != null && awayScore != null;

  let scoreboard;
  let eventTime;
  if (status === 'to_come' && gameDate) {
    const eventDate = new Date(gameDate);
    const time = new Date(gameDate);
    eventTime = time.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    scoreboard = (
      <div className="event-card_date">
        <div>
          {eventDate.toLocaleDateString([], {
            month: 'short',
            day: '2-digit',
          })}
        </div>
        <div>{eventTime}</div>
      </div>
    );
  } else if (status === 'to_come') {
    scoreboard = <div className="event-card_date">-</div>;
  } else if (homeScore == null && awayScore == null) {
    scoreboard = (
      <div
        className="event_card_give-score"
        dangerouslySetInnerHTML={{
          __html: 'Donner <br />le score',
        }}
      />
    );
  } else {
    scoreboard = (
      <div className="event-card_scoreboard">
        {status === 'live' && (
          <div className="event-card_scoreboard_live">
            <span>{status}</span>
          </div>
        )}
        <div className="event-card_scores_and_shoutouts">
          <div className="event-card_scores">
            <Score
              score={homeScore ?? undefined}
              isWinner={
                status === 'finish' && hasScores && homeScore > awayScore
              }
            />
            <Score
              score={awayScore ?? undefined}
              isWinner={
                status === 'finish' && hasScores && homeScore < awayScore
              }
            />
          </div>
          {homeShoutout && awayShoutout ? (
            <p className="event-card_scoreboard_shoutouts">
              TAB {homeShoutout} - {awayShoutout}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div css={style.root} {...rest}>
      <ClubLogo
        className="event-card_logo"
        logo={homeTeam?.team?.logo}
        name={homeTeam?.team?.name || homeTeam?.name}
        shieldColor="home"
        width={50}
        height={50}
      />

      <div className="event-card_team">
        {homeTeam?.team?.name || homeTeam?.name || <Skeleton />}
      </div>

      {scoreboard}

      <div className="event-card_team event-card_team-away">
        {awayTeam?.team?.name || awayTeam?.name || <Skeleton />}
      </div>

      <ClubLogo
        className="event-card_logo"
        logo={awayTeam?.team?.logo}
        name={awayTeam?.team?.name || homeTeam?.name}
        shieldColor="away"
        width={50}
        height={50}
      />
    </div>
  );
};
